import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Globe, Cpu, GraduationCap, Code2, Sparkles, MapPin, Clock, Briefcase } from 'lucide-react';

const openPositions = [
  {
    id: 'senior-fullstack',
    title: 'Senior Full-Stack Engineer',
    type: 'Full-time',
    location: 'Remote',
    department: 'Engineering',
    description: 'Build scalable SaaS platforms and AI-powered systems for high-growth companies generating $1M–$100M+ in revenue.',
  },
  {
    id: 'ai-engineer',
    title: 'AI / ML Engineer',
    type: 'Full-time',
    location: 'Remote',
    department: 'AI & Automation',
    description: 'Design and deploy intelligent agents, predictive models, and automation pipelines that eliminate bottlenecks and reduce costs by 40–70%.',
  },
  {
    id: 'growth-strategist',
    title: 'Growth Strategist',
    type: 'Full-time',
    location: 'Remote',
    department: 'Growth',
    description: 'Own acquisition strategy, CRO, and lifecycle automation — driving 2–5x customer growth with measurable ROI.',
  },
  {
    id: 'devops-engineer',
    title: 'DevOps / Cloud Engineer',
    type: 'Full-time',
    location: 'Remote',
    department: 'Infrastructure',
    description: 'Architect auto-scaling cloud infrastructure on AWS/GCP with 99.9% uptime SLAs for mission-critical SaaS platforms.',
  },
];

const highlights = [
  { icon: Globe, title: 'Remote-First', text: 'Work from anywhere with full autonomy.' },
  { icon: Cpu, title: 'High-Impact Work', text: 'Build systems that generate millions in revenue.' },
  { icon: GraduationCap, title: 'Fast Growth', text: 'Direct mentorship from senior engineers and founders.' },
  { icon: Code2, title: 'Modern Stack', text: 'LangChain, Python, React, Flutter, AWS/GCP.' },
];

const techStack = ['LangChain', 'Python', 'Flutter', 'React', 'AWS', 'GCP', 'TypeScript', 'FastAPI', 'Docker', 'Kubernetes', 'PostgreSQL', 'Redis'];

export default function CareersPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="careers-page" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Hero */}
      <div className="kv-container">
        <div className="pt-10 pb-8 lg:pt-12 lg:pb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium mb-6 transition-colors"
            style={{ color: '#475569' }}
            data-testid="careers-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-4" style={{ borderColor: '#E2E8F0', backgroundColor: 'rgba(10,61,145,0.03)' }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#00BFFF' }} />
              <span className="text-xs font-medium" style={{ color: '#0A3D91' }}>We're Hiring</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4" style={{ color: '#131B2E' }} data-testid="careers-page-headline">
              Join the Future of{' '}
              <span style={{ color: '#0A3D91' }}>Intelligence.</span>
            </h1>
            <p className="text-lg leading-relaxed" style={{ color: '#475569' }}>
              We partner with ambitious companies to build systems that generate millions in revenue.
              We're looking for exceptional engineers, strategists, and operators who want high-impact work.
            </p>
          </div>
        </div>
      </div>

      {/* Highlights Grid */}
      <div className="kv-container">
        <div className="pb-8 lg:pb-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {highlights.map((h) => (
              <div key={h.title} className="p-4 rounded-lg border" style={{ borderColor: '#E2E8F0' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(10,61,145,0.05)' }}>
                  <h.icon className="w-4 h-4" style={{ color: '#0A3D91' }} />
                </div>
                <p className="text-sm font-bold mb-1" style={{ color: '#131B2E' }}>{h.title}</p>
                <p className="text-xs" style={{ color: '#475569' }}>{h.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Open Positions */}
      <div className="kv-container">
        <div className="pb-10 lg:pb-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ color: '#131B2E' }} data-testid="open-positions-headline">
            Open Positions
          </h2>
          <div className="space-y-3" data-testid="positions-list">
            {openPositions.map((pos) => (
              <div
                key={pos.id}
                className="group p-5 rounded-xl border transition-all duration-200 hover:shadow-md"
                style={{ borderColor: '#E2E8F0' }}
                data-testid={`position-${pos.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold mb-1" style={{ color: '#131B2E' }}>{pos.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 text-xs" style={{ color: '#475569' }}>
                      <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{pos.department}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{pos.location}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{pos.type}</span>
                    </div>
                  </div>
                  <a
                    href={`mailto:careers@kritvasoft.com?subject=Application: ${pos.title}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 shrink-0"
                    style={{ backgroundColor: '#0A3D91', color: '#FFFFFF' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#083378'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0A3D91'}
                    data-testid={`apply-${pos.id}`}
                  >
                    Apply Now
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: '#475569' }}>{pos.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tech Stack */}
      <div className="kv-container">
        <div className="pb-8 lg:pb-10 border-t pt-6" style={{ borderColor: '#E2E8F0' }}>
          <p className="text-center text-xs uppercase tracking-wider mb-4" style={{ color: '#475569' }}>Our Technology Stack</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {techStack.map((tech) => (
              <span key={tech} className="px-3 py-1.5 rounded-full text-xs font-medium border" style={{ borderColor: '#E2E8F0', color: '#131B2E' }}>{tech}</span>
            ))}
          </div>
        </div>
      </div>

      {/* General Application CTA */}
      <div className="kv-container">
        <div className="pb-10 lg:pb-14 text-center">
          <p className="text-lg font-semibold mb-2" style={{ color: '#131B2E' }}>Don't see a perfect fit?</p>
          <p className="text-sm mb-4 max-w-md mx-auto" style={{ color: '#475569' }}>
            We're always looking for exceptional talent. Send us your resume and tell us what excites you.
          </p>
          <a
            href="mailto:careers@kritvasoft.com?subject=General Application"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg border-2 transition-all duration-200"
            style={{ borderColor: '#0A3D91', color: '#0A3D91' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0A3D91'; e.currentTarget.style.color = '#FFFFFF'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#0A3D91'; }}
            data-testid="general-application-cta"
          >
            Send General Application
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
